import { useState, useEffect, useCallback, useRef } from 'react';
import axiosClient from '../api/axiosClient';

/**
 * Custom hook for managing notifications
 * Provides loading, marking as read, and polling functionality
 */
export const useNotifications = () => {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  const pollingIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Calculate unread count from items
  useEffect(() => {
    const count = items ? items.filter(item => !item.read).length : 0;
    setUnreadCount(count);
  }, [items]);

  // Load notifications from API
  const load = useCallback(async ({ limit = 20, offset = 0 } = {}) => {
    // If not authenticated, skip loading to avoid noisy errors during logout
    const token = localStorage.getItem('token');
    if (!token) {
      setItems([]);
      setUnreadCount(0);
      setLoading(false);
      setError(null);
      return;
    }
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      if (typeof window !== 'undefined' && window.__suppressToasts) {
        // If global suppress flag is on, silently skip and clear
        setItems([]);
        setUnreadCount(0);
        setLoading(false);
        setError(null);
        return;
      }
      const response = await axiosClient.get('/notifications', {
        params: { limit, offset },
        signal: abortControllerRef.current.signal
      });

      if (response.data.success) {
        const { items: notifications, hasMore: hasMoreData } = response.data.data;
        
        if (offset === 0) {
          // Replace items for first load or refresh
          setItems(notifications || []);
        } else {
          // Append items for pagination
          setItems(prev => [...prev, ...(notifications || [])]);
        }
        
        setHasMore(hasMoreData);
      } else {
        throw new Error(response.data.error || 'Failed to load notifications');
      }
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        if (typeof window !== 'undefined' && window.__suppressToasts) {
          // During logout, swallow errors
          setLoading(false);
          return;
        }
        console.error('Error loading notifications:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a single notification as read
  const markRead = useCallback(async (id) => {
    const originalItems = [...items];
    
    // Optimistic update
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, read: true } : item
    ));

    try {
      const response = await axiosClient.patch(`/notifications/${id}/mark-read`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to mark notification as read');
      }
      
      // Update the specific item with server response
      setItems(prev => prev.map(item => 
        item.id === id ? { ...item, read: true } : item
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      
      // Rollback optimistic update
      setItems(originalItems);
      
      // Show non-blocking error
      setError(err.response?.data?.error || err.message || 'Failed to mark notification as read');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 5000);
    }
  }, [items]);

  // Mark all notifications as read
  const markAllRead = useCallback(async () => {
    const originalItems = [...items];
    
    // Optimistic update
    setItems(prev => prev.map(item => ({ ...item, read: true })));

    try {
      const response = await axiosClient.patch('/notifications/mark-all-read');
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to mark all notifications as read');
      }
      
      // Update all items as read
      setItems(prev => prev.map(item => ({ ...item, read: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      
      // Rollback optimistic update
      setItems(originalItems);
      
      // Show non-blocking error
      setError(err.response?.data?.error || err.message || 'Failed to mark all notifications as read');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 5000);
    }
  }, [items]);

  // Delete a single notification
  const deleteNotification = useCallback(async (id) => {
    const originalItems = [...items];
    
    // Optimistic update
    setItems(prev => prev.filter(item => item.id !== id));
    
    // Update unread count
    const deletedItem = items.find(item => item.id === id);
    if (deletedItem && !deletedItem.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      const response = await axiosClient.delete(`/notifications/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete notification');
      }
      
    } catch (err) {
      console.error('Error deleting notification:', err);
      
      // Rollback optimistic update
      setItems(originalItems);
      
      // Restore unread count
      if (deletedItem && !deletedItem.read) {
        setUnreadCount(prev => prev + 1);
      }
      
      // Show non-blocking error
      setError(err.response?.data?.error || err.message || 'Failed to delete notification');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 5000);
    }
  }, [items]);

  // Delete multiple notifications
  const deleteNotificationsBulk = useCallback(async (ids) => {
    const originalItems = [...items];
    
    // Optimistic update
    setItems(prev => prev.filter(item => !ids.includes(item.id)));
    
    // Update unread count
    const deletedUnreadCount = items.filter(item => 
      ids.includes(item.id) && !item.read
    ).length;
    setUnreadCount(prev => Math.max(0, prev - deletedUnreadCount));

    try {
      const response = await axiosClient.delete('/notifications/delete-bulk', {
        data: { ids }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete notifications');
      }
      
      return response.data;
      
    } catch (err) {
      console.error('Error deleting notifications:', err);
      
      // Rollback optimistic update
      setItems(originalItems);
      
      // Restore unread count
      setUnreadCount(prev => prev + deletedUnreadCount);
      
      // Show non-blocking error
      setError(err.response?.data?.error || err.message || 'Failed to delete notifications');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 5000);
      
      throw err;
    }
  }, [items]);

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    const originalItems = [...items];
    const originalUnreadCount = unreadCount;
    
    // Optimistic update
    setItems([]);
    setUnreadCount(0);

    try {
      const response = await axiosClient.delete('/notifications/delete-all');
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete all notifications');
      }
      
      return response.data;
      
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      
      // Rollback optimistic update
      setItems(originalItems);
      setUnreadCount(originalUnreadCount);
      
      // Show non-blocking error
      setError(err.response?.data?.error || err.message || 'Failed to delete all notifications');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 5000);
      
      throw err;
    }
  }, [items, unreadCount]);

  // Reload notifications (refresh)
  const reload = useCallback(() => {
    return load({ limit: 20, offset: 0 });
  }, [load]);

  // Start polling for new notifications
  const startPolling = useCallback((interval = 30000) => {
    // Clear existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Only poll for unread count, not full reload
    pollingIntervalRef.current = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (!token) return; // skip when logged out
      try {
        const response = await axiosClient.get('/notifications', {
          params: { limit: 20, offset: 0 },
          skipActivityTracking: true
        });
        
        if (response.data.success) {
          const { items: notifications } = response.data.data;
          
          // Only update if there are changes to avoid unnecessary re-renders
          const newUnreadCount = notifications ? notifications.filter(item => !item.read).length : 0;
          if (newUnreadCount !== unreadCount) {
            setItems(notifications || []);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
        // Don't show errors for polling failures
      }
    }, interval);
  }, [unreadCount]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    items,
    unreadCount,
    loading,
    error,
    hasMore,
    load,
    markRead,
    markAllRead,
    deleteNotification,
    deleteNotificationsBulk,
    deleteAllNotifications,
    reload,
    startPolling,
    stopPolling
  };
};
